import { useEffect, useState } from "react";
import { X as XIcon, Loader2, Check, ArrowRight } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useLang } from "../../lib/LangContext";
import { clubCopy } from "../../lib/clubCopy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Triggers
const SHOW_DELAY_MS = 20000;      // 20 seconds
const SCROLL_THRESHOLD = 0.3;     // 30% of the page

// LocalStorage keys
const LS_JOINED = "puregloss_club_joined";

// Global event to open the popup manually (from Navigation link)
export const OPEN_CLUB_EVENT = "puregloss:open-club";
export const openClubPopup = () => {
  try { window.dispatchEvent(new CustomEvent(OPEN_CLUB_EVENT)); } catch { /* ignore */ }
};

const hasJoined = () => {
  try { return localStorage.getItem(LS_JOINED) === "1"; } catch { return false; }
};

const markJoined = () => {
  try { localStorage.setItem(LS_JOINED, "1"); } catch { /* ignore */ }
};

const scrollProgress = () => {
  const doc = document.documentElement;
  const scrolled = window.scrollY || doc.scrollTop || 0;
  const total = (doc.scrollHeight || 0) - (window.innerHeight || 0);
  return total > 0 ? scrolled / total : 0;
};

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim());

export const ClubPopup = () => {
  const { t, lang } = useLang();
  const location = useLocation();
  const isPureGarage = location.pathname === "/app" || location.pathname.startsWith("/app/");
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Auto-trigger: 20 s OR 30 % scroll (whichever first). Never trigger if
  // the visitor has already joined the club on this browser.
  useEffect(() => {
    if (isPureGarage) return undefined;
    if (hasJoined()) return undefined;
    let done = false;
    const openOnce = () => {
      // Never interrupt an open assistant conversation.
      if (done || document.body.dataset.assistantOpen === "1" || document.body.dataset.bookingActive === "1" || sessionStorage.getItem("pg_booking_wizard")) return;
      done = true;
      setOpen(true);
    };

    const timer = setTimeout(openOnce, SHOW_DELAY_MS);
    const onScroll = () => {
      if (scrollProgress() >= SCROLL_THRESHOLD) openOnce();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();  // instant check in case the page is already past 30 %

    return () => {
      done = true;
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [isPureGarage]);

  // Manual open (from nav) — always honored unless the visitor has already
  // joined. Even then we still let the admin open it (they may want to look
  // at the design); joined browsers just don't auto-trigger.
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(OPEN_CLUB_EVENT, handler);
    return () => window.removeEventListener(OPEN_CLUB_EVENT, handler);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, submitting]);

  // Close only sets local state — no persisted cooldown.
  // Popup will re-appear on the next page visit for non-members.
  const close = () => setOpen(false);

  const scrollToContact = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    window.dispatchEvent(new CustomEvent("puregloss:set-flow", { detail: { flow: "price" } }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!isValidEmail(email)) { setError(t.club.emailInvalid); return; }
    if (!consent) { setError(t.club.gdprRequired); return; }
    setError("");
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/club/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim() || null,
          email: email.trim().toLowerCase(),
          language: lang,
          gdpr_accepted: true,
        }),
      });
      if (r.status === 409) {
        // Already a member — treat as a successful "join" so we never show
        // the popup again on this browser.
        markJoined();
        setError(t.club.alreadyRegistered);
        return;
      }
      if (!r.ok) {
        setError(t.club.genericError);
        return;
      }
      markJoined();
      setSuccess(true);
    } catch {
      setError(t.club.genericError);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  const k = clubCopy(lang);
  const inputCls = "w-full bg-white/[0.03] border border-white/12 rounded-xl px-4 py-3.5 text-white placeholder:text-zinc-500 focus:outline-none focus:border-gold/70 text-[15px] transition-colors";

  return (
    <div
      data-testid="club-popup"
      role="dialog"
      aria-modal="true"
      aria-labelledby="club-popup-title"
      className="fixed inset-0 z-[140] flex items-center justify-center bg-black/75 backdrop-blur-md p-3 sm:p-6"
      onClick={close}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[920px] max-h-[calc(100dvh-24px)] overflow-y-auto rounded-[20px] bg-[#0a0a0c] border border-white/10 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)] animate-in fade-in zoom-in-[0.98] duration-300"
      >
        <button
          type="button"
          onClick={close}
          data-testid="club-popup-close"
          disabled={submitting}
          aria-label={t.club.close}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full inline-flex items-center justify-center text-zinc-400 hover:text-white bg-white/[0.04] border border-white/10 hover:border-white/30 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-gold"
        >
          <XIcon size={16} />
        </button>

        <div className="grid md:grid-cols-[1.05fr_1fr]">
          <div className="relative p-7 sm:p-9 md:p-10 bg-gradient-to-br from-[#121214] to-[#0a0a0c] border-b md:border-b-0 md:border-r border-white/[0.07]">
            <span className="text-[11px] tracking-[0.35em] uppercase text-gold/90">PUREGLOSS CLUB</span>
            <div className="mt-6 flex items-end gap-4">
              <span className="font-heading text-white text-6xl md:text-7xl leading-none tracking-tight">{k.amount}</span>
              <span className="pb-2 text-[12px] tracking-[0.25em] uppercase text-zinc-400">{k.badge}</span>
            </div>
            <span className="block w-10 h-px bg-gold/70 mt-6" />
            <p className="mt-5 text-zinc-300 text-[15px] leading-relaxed max-w-sm">{k.offer}</p>
            <div className="mt-7 text-[11px] tracking-[0.3em] uppercase text-zinc-500">{k.benefitsTitle}</div>
            <ul className="mt-3 space-y-3">
              {k.benefits.map((b, i) => (
                <li key={b} data-testid={`club-benefit-${i}`} className="flex items-start gap-3 text-[14px] text-zinc-200 leading-relaxed">
                  <Check size={15} className="text-gold mt-0.5 shrink-0" /> {b}
                </li>
              ))}
            </ul>
          </div>

          <div className="p-7 sm:p-9 md:p-10">
            {!success ? (
              <>
                <h2 id="club-popup-title" data-testid="club-popup-title" className="font-heading text-white text-2xl md:text-[28px] tracking-tight leading-tight pr-10">
                  {t.club.title}
                </h2>
                <p className="mt-3 text-zinc-400 text-[14px] leading-relaxed">{t.club.subtitle}</p>
                <form onSubmit={submit} className="mt-7 space-y-3">
                  <input data-testid="club-input-name" type="text" aria-label={t.club.nameLabel} value={name} onChange={(e) => setName(e.target.value)} placeholder={t.club.nameLabel} className={inputCls} />
                  <input data-testid="club-input-email" type="email" required aria-label={t.club.emailLabel} value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.club.emailLabel + " *"} className={inputCls} />
                  <label className="flex items-start gap-3 pt-1 text-[13px] text-zinc-400 leading-relaxed cursor-pointer">
                    <input data-testid="club-input-gdpr" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#d4af37] shrink-0" />
                    <span>{t.club.gdpr}</span>
                  </label>
                  {error && <p data-testid="club-error" role="alert" className="text-red-400 text-[13px]">{error}</p>}
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="club-submit"
                    className="w-full mt-2 inline-flex items-center justify-center gap-3 rounded-full bg-gold text-black px-6 py-4 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-[#e8cf86] transition-colors disabled:opacity-50"
                  >
                    {submitting ? <><Loader2 size={14} className="animate-spin" /> {t.club.joining}</> : <>{k.cta} <ArrowRight size={14} /></>}
                  </button>
                  <p className="text-[12px] text-zinc-500 leading-relaxed">{k.finePrint}</p>
                </form>
              </>
            ) : (
              <div data-testid="club-success" className="py-2">
                <span className="inline-flex w-12 h-12 rounded-full border border-gold/50 bg-gold/10 items-center justify-center">
                  <Check size={20} className="text-gold" />
                </span>
                <h3 className="mt-5 font-heading text-white text-2xl md:text-[28px] tracking-tight leading-tight">{t.club.successTitle}</h3>
                <p className="mt-4 text-zinc-300 text-[15px] leading-relaxed">{k.success}</p>
                <button
                  type="button"
                  onClick={() => { setOpen(false); setTimeout(scrollToContact, 100); }}
                  data-testid="club-success-cta"
                  className="mt-8 w-full inline-flex items-center justify-center gap-3 rounded-full bg-gold text-black px-6 py-4 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-[#e8cf86] transition-colors"
                >
                  {t.club.successCta} <ArrowRight size={14} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
